// Trend tool request builders. Each returns `{ request, unwrap, calcIds }`
// (`calcIds` is parallel to the input `columnInstances`, mapping each
// input to the calc id baked into the request so the response transform
// can pick the right series back out).
//
// Multi-series capable: pass an array of columnInstances and one calc
// per instance is emitted into the same request. For row scope, all
// calcs share the same outer filter wrap so we still issue a single
// Orion request. Benchmark series remain wrapped in their own
// Comparison grouping per calc -- they must, per Orion's contract.
//
// Single-instance call sites can still pass `columnInstance` (singular);
// the builders normalize to an array internally.

import {
  CALC_SOURCES,
  COLUMN_CATALOG_MAP,
  ENTITY_SCOPABLE_GROUPINGS,
  ORION_ENTITY_GROUPINGS,
  getSeriesCounterpartSource
} from '../../../lib/constants'
import { resolveColumnOptions } from '../../../lib/calcResolve'

const TREND_CALC_PREFIX = 'trend'

function globalDateConfigFromConfig (config) {
  return {
    dateMode: config.dateMode,
    quickDate: config.quickDate,
    startDate: config.startDate,
    endDate: config.endDate
  }
}

/**
 * Resolves the catalog entry + series source for a column instance, or
 * null when the column has no series counterpart (so the caller can
 * disable the trigger gracefully).
 */
function resolveSeriesSource (columnInstance) {
  if (!columnInstance) return null
  const catalog = COLUMN_CATALOG_MAP[columnInstance.columnType]
  if (!catalog) return null
  // Material catalog entries leave `kind` undefined; derived / window /
  // comparison set it explicitly. Reject anything that's not material;
  // the trend tool only knows how to plumb single-source series calcs.
  if (catalog.kind && catalog.kind !== 'material') return null
  const seriesSourceName = getSeriesCounterpartSource(catalog.calcSource, columnInstance.options)
  if (!seriesSourceName) return null
  const seriesSource = CALC_SOURCES[seriesSourceName]
  if (!seriesSource) return null
  return { catalog, sourceName: catalog.calcSource, seriesSourceName, seriesSource }
}

function isBenchmarkSeriesSource (sourceName) {
  // Add new benchmark series sources here as the catalog grows.
  return sourceName === 'benchmarkPerformanceSeries'
}

// Build one series calc with a stable, request-unique id so the
// transform can map response nodes back to the originating instance.
// Benchmark series stay wrapped in their own Comparison grouping (one
// per calc -- safer than sharing a wrap, since the inner calc's
// returnStyle / options can differ across instances).
function buildSeriesCalc (columnInstance, returnStyle, config, calcId) {
  const resolved = resolveSeriesSource(columnInstance)
  if (!resolved) return null
  const globalDateConfig = globalDateConfigFromConfig(config)
  const opts = resolveColumnOptions(columnInstance.options || {}, globalDateConfig)
  const calc = resolved.seriesSource.buildCalculation(calcId, opts, returnStyle)
  if (isBenchmarkSeriesSource(resolved.seriesSourceName)) {
    return {
      id: `benchmarks_${calcId}`,
      $type: 'grouping',
      grouping: 'Comparison',
      calculations: [calc]
    }
  }
  return calc
}

// Normalize `{ columnInstances } | { columnInstance }` callers to a
// single array. Single-instance callers (and the old test fixtures)
// stay working with no churn.
function normalizeInstances ({ columnInstances, columnInstance }) {
  if (Array.isArray(columnInstances)) return columnInstances
  if (columnInstance) return [columnInstance]
  return []
}

// Normalize a `returnStyle`-or-`returnStyleFor` arg into a function.
// Per-series cumulative mode lives upstream in `TrendTool`; this is the
// plumbing that lets each instance carry its own returnStyle without
// rewriting every call site that still passes a single shared object.
function normalizeReturnStyleFor (arg) {
  if (typeof arg === 'function') return arg
  return () => arg
}

function buildAllCalcs (instances, returnStyleFor, config) {
  const calcs = []
  const calcIds = []
  for (let i = 0; i < instances.length; i++) {
    const inst = instances[i]
    const id = `${TREND_CALC_PREFIX}_${i}`
    const returnStyle = returnStyleFor(inst, i)
    const calc = buildSeriesCalc(inst, returnStyle, config, id)
    if (!calc) return null
    calcs.push(calc)
    calcIds.push(id)
  }
  return { calcs, calcIds }
}

/**
 * Level-scope request: chart each column instance's series at the
 * household level. No grouping wrapper; calcs sit at the top level (or
 * each wrapped in their own Comparison grouping for benchmark series).
 *
 * @returns `{ request, unwrap: null, calcIds }` or null when ineligible.
 */
export function buildLevelRequest ({ config, asOfDate, entity, entityIds, returnStyle, returnStyleFor, columnInstances, columnInstance }) {
  const instances = normalizeInstances({ columnInstances, columnInstance })
  if (!instances.length || !asOfDate || !entity || !entityIds) return null
  const styleFn = normalizeReturnStyleFor(returnStyleFor ?? returnStyle)
  const built = buildAllCalcs(instances, styleFn, config)
  if (!built) return null
  return {
    request: {
      entity,
      entityIds,
      asOfDate,
      calculations: built.calcs
    },
    unwrap: null,
    calcIds: built.calcIds
  }
}

/**
 * Row-scope request: chart each column instance's series for a specific
 * row's entity. Same dispatch as before -- entity-scopable rows just
 * change `entity`/`entityIds`; virtual-grouping rows scope to the
 * nearest entity-scopable ancestor and wrap calcs in a filter grouping.
 * Multiple calcs share the same filter wrap.
 *
 * @returns `{ request, unwrap, calcIds }` or null when ineligible.
 */
export function buildRowRequest ({ config, asOfDate, entity, entityIds, row, returnStyle, returnStyleFor, columnInstances, columnInstance }) {
  const instances = normalizeInstances({ columnInstances, columnInstance })
  if (!row || !instances.length || !asOfDate || !entity || !entityIds) return null
  for (const inst of instances) {
    if (!resolveSeriesSource(inst)) return null
  }
  const styleFn = normalizeReturnStyleFor(returnStyleFor ?? returnStyle)
  const built = buildAllCalcs(instances, styleFn, config)
  if (!built) return null

  const isScopable = ENTITY_SCOPABLE_GROUPINGS.has(row.grouping)

  if (isScopable) {
    return {
      request: {
        entity: row.grouping,
        entityIds: [row.groupEntityId],
        asOfDate,
        calculations: built.calcs
      },
      unwrap: null,
      calcIds: built.calcIds
    }
  }

  const ancestorEntity = row._ancestorEntity || entity
  const ancestorEntityIds = row._ancestorEntityIds || entityIds
  const filterPath = ORION_ENTITY_GROUPINGS.has(row.grouping)
    ? `group-info:${row.grouping}:Name`
    : 'group-info'

  return {
    request: {
      entity: ancestorEntity,
      entityIds: ancestorEntityIds,
      asOfDate,
      calculations: [{
        id: `filter_${row.grouping}`,
        $type: 'grouping',
        grouping: row.grouping,
        filters: [{
          $type: 'string-equal',
          path: filterPath,
          haystack: [row.name]
        }],
        calculations: built.calcs
      }]
    },
    unwrap: { name: row.name, groupEntityId: row.groupEntityId ?? null },
    calcIds: built.calcIds
  }
}
